import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck, MagnifyingGlass, Buildings, Briefcase, Calendar, IdentificationCard,
  Warning, Plus, Trash, QrCode, Download, ClockCounterClockwise, FileText,
} from "@phosphor-icons/react";

const STATUS_META = {
  VERIFIED_EMPLOYEE:     { label: "Verified Employee",  color: "#22C55E", icon: ShieldCheck },
  VERIFICATION_PENDING:  { label: "Verification Pending", color: "#F59E0B", icon: Warning },
  EMPLOYMENT_ENDED:      { label: "Employment Ended",  color: "#71717A", icon: Briefcase },
  NOT_VERIFIED:          { label: "Not Verified",      color: "#FF3B30", icon: Warning },
};

const CONDUCT_OPTIONS = [
  { value: "EXCELLENT", label: "Excellent", color: "#22C55E" },
  { value: "GOOD", label: "Good", color: "#22C55E" },
  { value: "SATISFACTORY", label: "Satisfactory", color: "#F59E0B" },
  { value: "NEEDS_IMPROVEMENT", label: "Needs Improvement", color: "#FF3B30" },
  { value: "NOT_ISSUED", label: "Not Issued", color: "#71717A" },
];

export default function Verify() {
  const { vid: paramVid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isHR = ["super_admin", "managing_director", "hr_manager"].includes(user?.role);

  const [vid, setVid] = useState(paramVid || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // consent flow
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentForm, setConsentForm] = useState({ company_name: "", purpose: "", accept: false });
  const [pendingVid, setPendingVid] = useState("");

  // qr
  const [qrOpen, setQrOpen] = useState(false);

  // employment / conduct (admin only)
  const [emp, setEmp] = useState(null);  // full employee record (admin)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyForm, setHistoryForm] = useState({
    company_name: "", designation: "", start_date: "", end_date: "",
    reason_for_leaving: "", verification_status: "PENDING",
  });
  const [conductOpen, setConductOpen] = useState(false);
  const [conductForm, setConductForm] = useState({ rating: "GOOD", remarks: "", certificate: null });

  // audit logs
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);

  const profileUrl = useMemo(() => {
    if (!result?.verification_id) return "";
    return `${window.location.origin}/verify/${result.verification_id}`;
  }, [result]);

  async function doLookup(target) {
    setBusy(true); setResult(null); setEmp(null);
    try {
      const { data } = await api.get(`/verify/${target}`);
      setResult(data);
      nav(`/verify/${target}`, { replace: true });
      if (isHR) {
        // fetch admin-only data to manage history / conduct
        try {
          const list = await api.get("/employees");
          const found = list.data.find((e) => e.verification_id === target);
          if (found) {
            const full = await api.get(`/employees/${found.id}`);
            setEmp(full.data);
          }
        } catch (_e) { /* ignore */ }
      }
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  }

  function startLookup(e) {
    if (e) e.preventDefault();
    const target = vid.trim().toUpperCase();
    if (!target) { toast.error("Enter a verification ID"); return; }
    // HR/SA records consent first (audit-compliant). External viewers can be added later.
    setPendingVid(target);
    setConsentForm({ company_name: "", purpose: "", accept: false });
    setConsentOpen(true);
  }

  async function confirmConsent() {
    if (!consentForm.accept) { toast.error("Tick the consent box to continue"); return; }
    if (!consentForm.purpose.trim()) { toast.error("Enter a purpose for this lookup"); return; }
    setConsentBusy(true);
    try {
      await api.post("/verify/consent", {
        verification_id: pendingVid,
        company_name: consentForm.company_name.trim(),
        purpose: consentForm.purpose.trim(),
        accept: true,
      });
      setConsentOpen(false);
      await doLookup(pendingVid);
    } catch (er) {
      toast.error(formatApiError(er));
    } finally { setConsentBusy(false); }
  }

  // ---------- admin-only: employment history
  async function addHistory() {
    if (!historyForm.company_name || !historyForm.designation || !historyForm.start_date) {
      toast.error("Company, designation and start date are required"); return;
    }
    try {
      await api.post(`/employees/${emp.id}/employment-history`, historyForm);
      toast.success("Employment history added");
      setHistoryOpen(false);
      setHistoryForm({ company_name: "", designation: "", start_date: "", end_date: "", reason_for_leaving: "", verification_status: "PENDING" });
      await doLookup(result.verification_id);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function removeHistory(itemId) {
    if (!window.confirm("Remove this history entry?")) return;
    try {
      await api.delete(`/employees/${emp.id}/employment-history/${itemId}`);
      toast.success("Removed");
      await doLookup(result.verification_id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // ---------- admin-only: conduct
  async function fileToDataUrl(file) {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  }
  async function saveConduct() {
    try {
      let cert = conductForm.certificate;
      if (cert instanceof File) cert = await fileToDataUrl(cert);
      await api.put(`/employees/${emp.id}/conduct`, {
        rating: conductForm.rating,
        remarks: conductForm.remarks,
        certificate: cert,
      });
      toast.success("Conduct saved");
      setConductOpen(false);
      await doLookup(result.verification_id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // ---------- admin-only: open existing conduct dialog (prefill)
  function openConductEditor() {
    setConductForm({
      rating: emp?.code_of_conduct?.rating || "GOOD",
      remarks: emp?.code_of_conduct?.remarks || "",
      certificate: null,
    });
    setConductOpen(true);
  }

  async function openLogs() {
    setLogsOpen(true);
    try {
      const { data } = await api.get("/verify-logs/all?limit=100");
      setLogs(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function downloadQr() {
    const svg = document.getElementById("ethiccred-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.verification_id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">EthicCred</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Employee Verification</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Look up any EthicHR employee by Global Verification ID. Only employment data is exposed;
            Aadhaar, PAN, Bank and Salary remain private. Every lookup is consent-logged.
          </p>
        </div>
        {isHR && (
          <Button onClick={openLogs} variant="outline" data-testid="open-verif-logs"
                  className="border-[#2D2D35] bg-transparent">
            <ClockCounterClockwise size={14} className="mr-1.5" /> Lookup logs
          </Button>
        )}
      </div>

      <form onSubmit={startLookup} className="mt-7 bg-[#16161C] border border-[#2D2D35] rounded-md p-5 flex flex-col md:flex-row gap-3 items-end">
        <div className="flex-1 w-full">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Verification ID</Label>
          <div className="relative mt-2">
            <MagnifyingGlass size={14} className="absolute left-3 top-3.5 text-zinc-500" />
            <Input value={vid} onChange={(e) => setVid(e.target.value)}
                   placeholder="e.g. ETH-IND-7X9K2P4M"
                   data-testid="verify-input"
                   className="pl-9 bg-[#0D0D10] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914] uppercase tracking-wider font-mono" />
          </div>
        </div>
        <Button type="submit" data-testid="verify-search-btn" disabled={busy || consentBusy}
                className="bg-[#E50914] hover:bg-[#C80812] h-10 px-6">
          {busy ? "Searching…" : "Verify"}
        </Button>
      </form>

      {result && (() => {
        const meta = STATUS_META[result.display_status] || STATUS_META.NOT_VERIFIED;
        const Icon = meta.icon;
        return (
          <div className="mt-6 bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden"
               data-testid="verify-result">
            <div className="px-6 py-5 border-b border-[#2D2D35] flex items-start gap-5 flex-wrap">
              {result.photo ? (
                <img src={result.photo} alt="" className="h-20 w-20 rounded-md object-cover border border-[#2D2D35]" />
              ) : (
                <div className="h-20 w-20 rounded-md bg-[#0D0D10] border border-[#2D2D35] flex items-center justify-center">
                  <IdentificationCard size={28} className="text-zinc-500" />
                </div>
              )}
              <div className="flex-1 min-w-[240px]">
                <div className="font-display text-2xl">{result.name || "—"}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{result.designation || "—"} · {result.department || "—"}</div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">
                  Verification ID: <span className="text-white">{result.verification_id}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setQrOpen(true)}
                        data-testid="show-qr-btn" className="border-[#2D2D35] bg-transparent">
                  <QrCode size={14} className="mr-1.5" /> QR
                </Button>
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded font-semibold"
                     style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
                     data-testid="verify-status">
                  <Icon size={14} weight="fill" /> {meta.label}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2D2D35]">
              <Card icon={Buildings} label="Current Company" value={result.company} />
              <Card icon={Calendar} label="Joined" value={(result.joining_date || "").slice(0, 10) || "—"} />
              <Card icon={Briefcase} label="Experience" value={`${result.experience_years || 0} years`} />
              <Card icon={IdentificationCard} label="Employment Type" value={(result.employment_type || "—").replaceAll("_", " ")} />
              <Card icon={ShieldCheck} label="Conduct"
                value={result.code_of_conduct?.rating?.replaceAll("_", " ") || "Not issued"}
                color={result.code_of_conduct?.rating === "EXCELLENT" ? "#22C55E" :
                       result.code_of_conduct?.rating === "GOOD" ? "#22C55E" :
                       result.code_of_conduct?.rating === "NEEDS_IMPROVEMENT" ? "#FF3B30" : undefined} />
              <Card icon={Briefcase} label="Status" value={result.active ? "Active" : "Ended"}
                    color={result.active ? "#22C55E" : "#71717A"} />
            </div>

            {/* Employment history */}
            <div className="px-6 py-5 border-t border-[#2D2D35]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Employment History</div>
                {isHR && emp && (
                  <Button size="sm" onClick={()=>setHistoryOpen(true)} data-testid="add-history-btn"
                          className="bg-[#E50914] hover:bg-[#C80812] h-8 px-3 text-xs">
                    <Plus size={12} className="mr-1" /> Add
                  </Button>
                )}
              </div>
              {(result.employment_history || []).length === 0 && (
                <div className="text-xs text-zinc-500">No prior employment recorded.</div>
              )}
              <div className="space-y-2">
                {(result.employment_history || []).map((h) => (
                  <div key={h.id || h.company_name} className="flex items-center justify-between bg-[#0D0D10] border border-[#2D2D35] rounded p-3">
                    <div>
                      <div className="text-sm">{h.designation} · <span className="text-zinc-400">{h.company_name}</span></div>
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {(h.start_date || "").slice(0,10)} → {(h.end_date || "").slice(0,10) || "Present"}
                      </div>
                      {h.reason_for_leaving && <div className="text-[10px] text-zinc-500 mt-0.5">{h.reason_for_leaving}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-1 rounded bg-[#0D0D10] border border-[#2D2D35]">{h.verification_status}</span>
                      {isHR && emp && (
                        <button onClick={()=>removeHistory(h.id)} className="p-1.5 text-zinc-500 hover:text-[#FF3B30]"
                                data-testid={`del-history-${h.id}`} title="Remove">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Code of conduct */}
            <div className="px-6 py-5 border-t border-[#2D2D35]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Code of Conduct</div>
                {isHR && emp && (
                  <Button size="sm" onClick={openConductEditor} data-testid="edit-conduct-btn"
                          className="bg-[#E50914] hover:bg-[#C80812] h-8 px-3 text-xs">
                    {emp?.code_of_conduct ? "Update" : "Issue Certificate"}
                  </Button>
                )}
              </div>
              {emp?.code_of_conduct ? (
                <div className="bg-[#0D0D10] border border-[#2D2D35] rounded p-3 space-y-1">
                  <div className="text-sm">
                    Rating: <span className="font-semibold">{(emp.code_of_conduct.rating || "").replaceAll("_"," ")}</span>
                  </div>
                  {emp.code_of_conduct.remarks && (
                    <div className="text-xs text-zinc-400">{emp.code_of_conduct.remarks}</div>
                  )}
                  {emp.code_of_conduct.certificate && (
                    <a href={emp.code_of_conduct.certificate} target="_blank" rel="noreferrer"
                       className="inline-flex items-center text-xs text-[#E50914] hover:underline mt-2"
                       data-testid="view-conduct-cert">
                      <FileText size={12} className="mr-1" /> View certificate
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-xs text-zinc-500">No conduct rating issued yet.</div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#2D2D35] text-[10px] text-zinc-500 font-mono">
              Sensitive PII (Aadhaar / PAN / Bank / Passport / Address / Salary) is never exposed via verification. Lookup logged.
            </div>
          </div>
        );
      })()}

      {/* Consent Modal */}
      <Dialog open={consentOpen} onOpenChange={(v)=>{ if (!consentBusy) setConsentOpen(v); }}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#E50914]" /> Consent & Purpose
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              You are about to view employment data for <span className="font-mono text-white">{pendingVid}</span>.
              This request will be logged with your name, company, IP and purpose.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Requesting Company</Label>
              <Input value={consentForm.company_name}
                     onChange={(e)=>setConsentForm({...consentForm, company_name:e.target.value})}
                     placeholder="e.g. Acme Hospitals"
                     data-testid="consent-company"
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Purpose</Label>
              <Textarea value={consentForm.purpose}
                        onChange={(e)=>setConsentForm({...consentForm, purpose:e.target.value})}
                        rows={2}
                        placeholder="e.g. Pre-employment background check"
                        data-testid="consent-purpose"
                        className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
            </div>
            <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={consentForm.accept}
                     onChange={(e)=>setConsentForm({...consentForm, accept:e.target.checked})}
                     data-testid="consent-accept"
                     className="mt-1 accent-[#E50914]" />
              I confirm the employee has authorised this verification request and I will not share
              the data outside this stated purpose.
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <Button variant="outline" disabled={consentBusy} onClick={()=>setConsentOpen(false)}
                    className="border-[#2D2D35] bg-transparent">Cancel</Button>
            <Button onClick={confirmConsent} disabled={consentBusy || !consentForm.accept}
                    data-testid="consent-submit"
                    className="bg-[#E50914] hover:bg-[#C80812]">
              {consentBusy ? "Recording…" : "Continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Verification QR</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-4">
            {result?.verification_id && (
              <div className="bg-white p-4 rounded">
                <QRCodeSVG id="ethiccred-qr-svg" value={profileUrl} size={200} level="H" includeMargin />
              </div>
            )}
            <div className="text-[10px] font-mono text-zinc-400 break-all text-center px-3">{profileUrl}</div>
            <Button variant="outline" onClick={downloadQr} data-testid="download-qr-btn"
                    className="border-[#2D2D35] bg-transparent">
              <Download size={14} className="mr-1.5" /> Download SVG
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Employment History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-lg">
          <DialogHeader><DialogTitle>Add Previous Employment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <SimpleField label="Company" value={historyForm.company_name}
                         onChange={(v)=>setHistoryForm({...historyForm, company_name:v})}
                         testid="history-company" />
            <SimpleField label="Designation" value={historyForm.designation}
                         onChange={(v)=>setHistoryForm({...historyForm, designation:v})}
                         testid="history-designation" />
            <SimpleField label="Start Date" type="date" value={historyForm.start_date}
                         onChange={(v)=>setHistoryForm({...historyForm, start_date:v})}
                         testid="history-start" />
            <SimpleField label="End Date" type="date" value={historyForm.end_date}
                         onChange={(v)=>setHistoryForm({...historyForm, end_date:v})}
                         testid="history-end" />
            <div className="col-span-2">
              <SimpleField label="Reason for leaving" value={historyForm.reason_for_leaving}
                           onChange={(v)=>setHistoryForm({...historyForm, reason_for_leaving:v})} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Verification Status</Label>
              <Select value={historyForm.verification_status}
                      onValueChange={(v)=>setHistoryForm({...historyForm, verification_status:v})}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                  {["VERIFIED","PENDING","UNVERIFIED"].map((s)=>(
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <Button variant="outline" onClick={()=>setHistoryOpen(false)} className="border-[#2D2D35] bg-transparent">Cancel</Button>
            <Button onClick={addHistory} data-testid="history-save-btn"
                    className="bg-[#E50914] hover:bg-[#C80812]">Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conduct dialog */}
      <Dialog open={conductOpen} onOpenChange={setConductOpen}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-lg">
          <DialogHeader><DialogTitle>Code of Conduct Certificate</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Rating</Label>
              <Select value={conductForm.rating} onValueChange={(v)=>setConductForm({...conductForm, rating:v})}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="conduct-rating-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                  {CONDUCT_OPTIONS.map((c)=>(
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Remarks</Label>
              <Textarea value={conductForm.remarks}
                        onChange={(e)=>setConductForm({...conductForm, remarks:e.target.value})}
                        rows={3} placeholder="Internal note (will be visible to verifiers)"
                        data-testid="conduct-remarks"
                        className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Upload Certificate (PDF / image, optional)</Label>
              <Input type="file" accept="application/pdf,image/*"
                     onChange={(e)=>setConductForm({...conductForm, certificate:e.target.files?.[0] || null})}
                     data-testid="conduct-file"
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <Button variant="outline" onClick={()=>setConductOpen(false)} className="border-[#2D2D35] bg-transparent">Cancel</Button>
            <Button onClick={saveConduct} data-testid="conduct-save-btn"
                    className="bg-[#E50914] hover:bg-[#C80812]">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit logs */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Verification Lookup Logs</DialogTitle>
          </DialogHeader>
          <div className="mt-2 border border-[#2D2D35] rounded overflow-hidden">
            <table className="w-full" data-testid="verif-logs-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
                  <th className="text-left p-2.5">When</th>
                  <th className="text-left p-2.5">Action</th>
                  <th className="text-left p-2.5">Verif. ID</th>
                  <th className="text-left p-2.5">Viewer</th>
                  <th className="text-left p-2.5">Company / Purpose</th>
                  <th className="text-left p-2.5">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id || (l.at + l.target)} className="border-b border-[#2D2D35]/60">
                    <td className="p-2.5 text-[11px] font-mono">{(l.at||"").replace("T"," ").slice(0,19)}</td>
                    <td className="p-2.5 text-[11px]">
                      <span className={`px-2 py-0.5 rounded text-[10px] ${l.action==='VERIFY_CONSENT' ? 'bg-[#22C55E]/15 text-[#22C55E]' : 'bg-[#E50914]/15 text-[#E50914]'}`}>
                        {l.action}
                      </span>
                    </td>
                    <td className="p-2.5 text-[11px] font-mono">{l.target}</td>
                    <td className="p-2.5 text-[11px]">
                      <div>{l.viewer_name}</div>
                      <div className="text-zinc-500">{l.viewer_email}</div>
                    </td>
                    <td className="p-2.5 text-[11px]">
                      <div>{l.meta?.company || "—"}</div>
                      <div className="text-zinc-500">{l.meta?.purpose || ""}</div>
                    </td>
                    <td className="p-2.5 text-[11px] font-mono text-zinc-400">{l.meta?.ip || "—"}</td>
                  </tr>
                ))}
                {!logs.length && (
                  <tr><td colSpan={6} className="text-center text-zinc-500 p-6 text-sm">No lookups yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ icon: Icon, label, value, color = "#FFFFFF" }) {
  return (
    <div className="bg-[#16161C] p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
        <Icon size={12} className="text-[#E50914]" /> {label}
      </div>
      <div className="font-mono text-base mt-2" style={{ color }}>{value || "—"}</div>
    </div>
  );
}

function SimpleField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={value ?? ""} type={type} onChange={(e)=>onChange(e.target.value)}
             data-testid={testid}
             className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
    </div>
  );
}
